import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { config } from '../../config/config';
import { DEFAULT_CHANGE_ORDER_TEMPLATE, generateItemsHtml, generateChangeOrderTemplate } from '../../data/defaultChangeOrderTemplate';
import { resizeImage } from '../../utils/imageUtils';
import Dialog from '../common/Dialog';
import { showToast } from '../utils/Toast';
import html2pdf from 'html2pdf.js';

const FloatingHeader = ({ onPrint }) => (
  <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 flex justify-between items-center">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Change Order Review</h1>
        <p className="mt-2 text-sm text-gray-600">
          Please review the change order details below and provide your signature at the bottom to approve.
        </p>
      </div>
      <button
        onClick={onPrint}
        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
        </svg>
        Print PDF
      </button>
    </div>
  </div>
);

const ConfirmationDialog = ({ isOpen, onClose, title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel', disableConfirm = false, disableCancel = false }) => {
  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-4">
          <button
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            onClick={onClose}
            disabled={disableCancel}
          >
            {cancelText}
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            disabled={disableConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

const StepsGuide = () => {
  const scrollToSignature = () => {
    const signatureSection = document.getElementById('signature-section');
    if (signatureSection) {
      signatureSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="fixed right-8 top-32 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Steps to Sign</h3>
      <ol className="space-y-4">
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            1
          </span>
          <span className="text-sm text-gray-600">
            Review the change order details carefully
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            2
          </span>
          <button 
            onClick={scrollToSignature}
            className="text-sm text-blue-600 hover:text-blue-800 text-left"
          >
            Click here to go to the signature section
          </button>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            3
          </span>
          <span className="text-sm text-gray-600">
            Enter your full name in the name field
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            4
          </span>
          <span className="text-sm text-gray-600">
            Verify the date is correct
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            5
          </span>
          <span className="text-sm text-gray-600">
            Draw your signature in the signature box
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            6
          </span>
          <span className="text-sm text-gray-600">
            Click "Approve Change Order" to complete
          </span>
        </li>
      </ol>
    </div>
  );
};

const formatCurrency = (amount) => {
  return Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const ChangeOrderView = () => {
  const { id } = useParams();
  const [changeOrder, setChangeOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const signatureRef = useRef();
  const changeOrderContentRef = useRef();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmReject, setShowConfirmReject] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    const fetchChangeOrder = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.apiUrl}/api/change-orders/${id}/public`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch change order');
        }

        if (!data.changeOrder) {
          throw new Error('Change order not found');
        }

        console.log('Fetched change order:', data.changeOrder);
        setChangeOrder(data.changeOrder);
      } catch (error) {
        console.error('Error fetching change order:', error);
        setError(error.message);
        showToast(error.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchChangeOrder();
    }
  }, [id]);

  const renderChangeOrderTemplate = () => {
    if (!changeOrder) return '';

    const project = changeOrder.project || {};

    // Format address properly by checking all possible locations
    const formattedAddress = [
      // Try site contact address first
      project.siteContact?.address?.street,
      project.siteContact?.address?.city,
      project.siteContact?.address?.state,
      project.siteContact?.address?.zipCode,
    ].filter(Boolean).join(', ') || 
    // If no site contact address, try client address
    [
      project.client?.address?.street,
      project.client?.address?.city,
      project.client?.address?.state,
      project.client?.address?.zipCode,
    ].filter(Boolean).join(', ') ||
    // Finally fall back to project address
    [
      project.address?.street,
      project.address?.city,
      project.address?.state,
      project.address?.zipCode,
    ].filter(Boolean).join(', ');

    // Get client name from various possible locations
    const clientName = project.siteContact?.name || 
                      project.client?.name || 
                      project.leadContact?.name || 
                      '';

    // Ensure all display settings have default values
    const displaySettings = {
      showScopeDescription: true,
      showCostCodeDescription: true,
      showQuantity: false,
      showUnit: false,
      showPrice: true,
      showCostCodes: false,
      expandGroups: false,
      separateGroups: false,
      ...changeOrder.displaySettings
    };

    console.log('Using display settings:', displaySettings);

    // Process scope items
    const scopeItems = (changeOrder.scopeOfWork || []).map(item => ({
      type: 'scope',
      item: item.costGroup?.name || item.item || '',
      description: item.costGroup?.description || item.description || '',
      quantity: Number(item.quantity || 1),
      unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
      clientPrice: Number(item.clientPrice || item.costGroup?.clientPrice || 0),
      costCodes: item.costCodes || []
    }));

    // Process standalone cost code items
    const costCodeItems = displaySettings.showCostCodes ? 
      (changeOrder.costCodes || []).filter(item => !item.costGroup).map(item => {
        // Log the complete item structure for debugging
        console.log('Raw cost code item structure:', JSON.stringify(item, null, 2));
        
        // Extract the item name with better fallback handling
        // First try costItem.item, then item.item, then fallback to 'Cost Code'
        let itemName = 'Cost Code';
        
        if (item.costItem && typeof item.costItem === 'object') {
          // If costItem is an object with an item property
          itemName = item.costItem.item || itemName;
        } else if (item.item) {
          // Direct item property
          itemName = item.item;
        }
        
        console.log('Extracted item name for cost code:', {
          itemId: item._id,
          extractedName: itemName,
          costItem: item.costItem
        });
        
        return {
          type: 'cost',
          item: itemName,
          description: item.costItem?.description || item.description || '',
          quantity: Number(item.quantity || 1),
          unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
          clientPrice: Number(item.clientPrice || 0)
        };
      }) : [];

    console.log('Scope items:', scopeItems.length);
    console.log('Standalone cost code items:', costCodeItems.length);
    console.log('Standalone cost code item names:', costCodeItems.map(item => item.item));
    
    // Log the raw data to help with debugging
    console.log('Raw changeOrder.costCodes:', changeOrder.costCodes);

    const allItems = [...scopeItems, ...costCodeItems];

    // Create a data object with all the project and change order info
    const projectData = {
      ...project,
      clientName,
      streetAddress: formattedAddress
    };

    const changeOrderData = {
      ...changeOrder,
      title: changeOrder.title || `Change Order for ${clientName}`,
      totals: {
        ...changeOrder.totals,
        clientPrice: formatCurrency(changeOrder.totals?.clientPrice || 0)
      }
    };

    let html = generateChangeOrderTemplate(projectData, changeOrderData, allItems, displaySettings);

    // Format any remaining currency values in the HTML
    html = html.replace(/\$(\d+\.\d{2})/g, (match, amount) => {
      return `$${formatCurrency(parseFloat(amount))}`;
    });

    return html;
  };

  const handleApprove = async () => {
    if (!name || !signatureRef.current.toData().length) {
      setErrorMessage('Please provide your name and signature');
      setShowErrorDialog(true);
      return;
    }

    try {
      setIsApproving(true);
      
      const originalSignature = signatureRef.current.toDataURL();
      
      const compressedSignature = await resizeImage(originalSignature, {
        maxWidth: 600,
        maxHeight: 300,
        quality: 0.5,
        type: 'image/jpeg'
      });

      const signatureData = {
        name,
        date,
        signature: compressedSignature
      };

      // Before submitting, let's also update the signature fields in the template
      if (changeOrderContentRef.current) {
        const signatureFields = changeOrderContentRef.current.querySelectorAll('.border-b.border-gray-300.pb-2');
        
        if (signatureFields.length >= 3) {
          // Insert signature image
          const signatureField = signatureFields[0];
          const signatureImg = document.createElement('img');
          signatureImg.src = compressedSignature;
          signatureImg.alt = 'Signature';
          signatureImg.className = 'h-16';
          signatureField.querySelector('div').innerHTML = '';
          signatureField.querySelector('div').appendChild(signatureImg);
          
          // Insert date
          const dateField = signatureFields[1];
          dateField.querySelector('div').textContent = new Date(date).toLocaleDateString();
          
          // Insert name
          const nameField = signatureFields[2];
          nameField.querySelector('div').textContent = name;
        }
      }

      const response = await fetch(`${config.apiUrl}/api/change-orders/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(signatureData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve change order');
      }

      // Update only the signature and status from the response
      setChangeOrder(prev => ({
        ...prev,
        status: data.changeOrder.status,
        signature: data.changeOrder.signature
      }));
      
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Approval error:', error);
      setErrorMessage(error.message);
      setShowErrorDialog(true);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsRejecting(true);
      
      const response = await fetch(`${config.apiUrl}/api/change-orders/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to reject change order');
      }

      // Update only the status from the response
      setChangeOrder(prev => ({
        ...prev,
        status: data.changeOrder.status
      }));
      
      setShowSuccessDialog(true);
      
    } catch (error) {
      console.error('Rejection error:', error);
      setErrorMessage(error.message);
      setShowErrorDialog(true);
    } finally {
      setIsRejecting(false);
    }
  };

  const renderExistingSignature = () => {
    if (changeOrder?.signature?.name && changeOrder?.signature?.signatureImage) {
      return (
        <div className="mt-4">
          <p className="text-sm text-gray-600">Signed by {changeOrder.signature.name}</p>
          <p className="text-sm text-gray-600">
            on {new Date(changeOrder.signature.date).toLocaleDateString()}
          </p>
          <img 
            src={changeOrder.signature.signatureImage} 
            alt="Signature" 
            className="mt-2 border border-gray-300 rounded-md"
          />
        </div>
      );
    }
    return null;
  };

  const handlePrint = async () => {
    if (!changeOrderContentRef.current || isPrinting) return;

    setIsPrinting(true);

    // Insert signature into the template before printing
    const element = changeOrderContentRef.current;
    
    // Find the signature fields in the template
    const signatureFields = element.querySelectorAll('.border-b.border-gray-300.pb-2');
    
    if (signatureFields.length >= 3 && changeOrder.signature) {
      // Insert signature image
      if (changeOrder.signature.signatureImage) {
        const signatureField = signatureFields[0];
        const signatureImg = document.createElement('img');
        signatureImg.src = changeOrder.signature.signatureImage;
        signatureImg.alt = 'Signature';
        signatureImg.className = 'h-16';
        signatureField.querySelector('div').innerHTML = '';
        signatureField.querySelector('div').appendChild(signatureImg);
      }
      
      // Insert date
      const dateField = signatureFields[1];
      dateField.querySelector('div').textContent = new Date(changeOrder.signature.date).toLocaleDateString();
      
      // Insert name
      const nameField = signatureFields[2];
      nameField.querySelector('div').textContent = changeOrder.signature.name;
    }

    const opt = {
      margin: [10, 10],
      filename: `change-order-${id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setIsPrinting(false);
      
      // Reset any changes we made for printing
      if (changeOrder.signature) {
        element.querySelectorAll('.border-b.border-gray-300.pb-2').forEach(field => {
          field.querySelector('div').innerHTML = '';
        });
      }
    }
  };

  // Add this function to insert signature into template fields after component renders
  useEffect(() => {
    // Only run after the change order is loaded and if there's a signature
    if (changeOrder && changeOrder.signature && changeOrderContentRef.current) {
      const element = changeOrderContentRef.current;
      const signatureFields = element.querySelectorAll('.border-b.border-gray-300.pb-2');
      
      if (signatureFields.length >= 3) {
        // Insert signature image
        if (changeOrder.signature.signatureImage) {
          const signatureField = signatureFields[0];
          const signatureImg = document.createElement('img');
          signatureImg.src = changeOrder.signature.signatureImage;
          signatureImg.alt = 'Signature';
          signatureImg.className = 'h-16';
          signatureField.querySelector('div').innerHTML = '';
          signatureField.querySelector('div').appendChild(signatureImg);
        }
        
        // Insert date
        const dateField = signatureFields[1];
        dateField.querySelector('div').textContent = new Date(changeOrder.signature.date).toLocaleDateString();
        
        // Insert name
        const nameField = signatureFields[2];
        nameField.querySelector('div').textContent = changeOrder.signature.name;
      }
    }
  }, [changeOrder]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!changeOrder) return <div>Change order not found</div>;

  return (
    <>
      <FloatingHeader onPrint={handlePrint} />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 py-8 mt-24">
        <div className="grid grid-cols-[1fr,320px] gap-8">
          <div className="space-y-8">
            <div ref={changeOrderContentRef} className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div dangerouslySetInnerHTML={{ __html: renderChangeOrderTemplate() }} />
              </div>
            </div>

            <div id="signature-section" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {changeOrder.status === 'customer_approved' ? 'Approval Details' : 'Sign to Approve'}
              </h2>

              {changeOrder.status === 'customer_approved' ? (
                <div>
                  <h3 className="text-lg font-medium text-green-600">Change Order Approved</h3>
                  {renderExistingSignature()}
                </div>
              ) : changeOrder.status === 'customer_rejected' ? (
                <div>
                  <h3 className="text-lg font-medium text-red-600">Change Order Rejected</h3>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Your Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Signature</label>
                    <div className="border border-gray-300 rounded-md bg-white">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'signature-canvas w-full h-40'
                        }}
                      />
                    </div>
                    <button
                      onClick={() => signatureRef.current.clear()}
                      className="mt-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear Signature
                    </button>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <button
                      onClick={handleApprove}
                      disabled={isApproving}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed flex justify-center items-center"
                    >
                      {isApproving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : "Approve Change Order"}
                    </button>
                    <button
                      onClick={() => setShowConfirmReject(true)}
                      disabled={isRejecting}
                      className="flex-1 bg-white text-gray-700 px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex justify-center items-center"
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : "Reject Change Order"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!changeOrder.status.includes('approved') && !changeOrder.status.includes('rejected') && (
            <div className="h-fit sticky top-32">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Steps to Sign</h3>
                <ol className="space-y-4">
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      1
                    </span>
                    <span className="text-sm text-gray-600">
                      Review the change order details carefully
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      2
                    </span>
                    <button 
                      onClick={() => {
                        const signatureSection = document.getElementById('signature-section');
                        if (signatureSection) {
                          signatureSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 text-left"
                    >
                      Click here to go to the signature section
                    </button>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      3
                    </span>
                    <span className="text-sm text-gray-600">
                      Enter your full name in the name field
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      4
                    </span>
                    <span className="text-sm text-gray-600">
                      Verify the date is correct
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      5
                    </span>
                    <span className="text-sm text-gray-600">
                      Draw your signature in the signature box
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      6
                    </span>
                    <span className="text-sm text-gray-600">
                      Click "Approve Change Order" to complete
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Success"
        message="Operation completed successfully!"
        confirmText="OK"
        onConfirm={() => setShowSuccessDialog(false)}
      />

      <ConfirmationDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        onConfirm={() => setShowErrorDialog(false)}
      />

      <ConfirmationDialog
        isOpen={showConfirmReject}
        onClose={() => !isRejecting && setShowConfirmReject(false)}
        title="Confirm Rejection"
        message="Are you sure you want to reject this change order?"
        confirmText={isRejecting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : "Reject"}
        cancelText="Cancel"
        onConfirm={handleReject}
        disableConfirm={isRejecting}
        disableCancel={isRejecting}
      />
    </>
  );
};

export default ChangeOrderView; 